import type { CompositeScenarioEvent } from '@/data/shared/safetyCompositeScenario';

/**
 * ### CompositeGroupingMode
 *
 * Whether timeline rows are one per sound type or one per microphone, the
 * two views the console offers.
 */
export type CompositeGroupingMode = 'label' | 'mic';

/**
 * ### CompositeTick
 *
 * One second mark of the timeline header, as a percentage across the row.
 */
export type CompositeTick = {
  position: number;
  value: number;
};

/**
 * ### LayeredEvent
 *
 * A detection with the row layer it was assigned to, so detections that
 * overlap in time stack instead of hiding one another.
 */
export type LayeredEvent = {
  event: CompositeScenarioEvent;
  layerIndex: number;
};

/**
 * ### CompositeRow
 *
 * One timeline row: its group key, its detections with layers, and how
 * many layers it needs.
 */
export type CompositeRow = {
  groupKey: string;
  events: ReadonlyArray<LayeredEvent>;
  layerCount: number;
};

/**
 * ### timelineTicks
 *
 * Second marks for a timeline of the given length: one per second up to
 * ten, then ten spread evenly. The console's own rule.
 *
 * @param durationSec Timeline length in seconds
 * @returns Ticks from left to right
 */
export function timelineTicks(durationSec: number): Array<CompositeTick> {
  const maxTicks = 10;
  const total = durationSec + 1;

  if (total <= maxTicks) {
    return Array.from({ length: total }, (_, index) => ({
      position: (index / durationSec) * 100,
      value: index,
    }));
  }

  return Array.from({ length: maxTicks }, (_, index) => {
    const value = Math.round((index / (maxTicks - 1)) * durationSec);

    return { position: (value / durationSec) * 100, value };
  });
}

/**
 * ### groupTimelineEvents
 *
 * Groups detections into rows by sound type or microphone, orders the rows
 * by their earliest detection, and assigns each detection the first layer
 * whose previous clip has ended before it starts.
 *
 * @param events Detections to lay out
 * @param mode Row grouping
 * @param clipSec Drawn length of every clip
 * @returns Rows in display order
 */
export function groupTimelineEvents(
  events: ReadonlyArray<CompositeScenarioEvent>,
  mode: CompositeGroupingMode,
  clipSec: number,
): Array<CompositeRow> {
  const groups = new Map<string, Array<CompositeScenarioEvent>>();

  events.forEach((event) => {
    const key = mode === 'label' ? event.label : event.micId;
    groups.set(key, [...(groups.get(key) ?? []), event]);
  });

  return Array.from(groups.entries())
    .map(([groupKey, grouped]) => {
      const sorted = [...grouped].sort((a, b) => a.startSec - b.startSec);
      const layerEnds: Array<number> = [];
      const layered = sorted.map((event): LayeredEvent => {
        let layerIndex = layerEnds.findIndex((end) => end <= event.startSec);
        if (layerIndex === -1) {
          layerIndex = layerEnds.length;
          layerEnds.push(event.startSec + clipSec);
        } else {
          layerEnds[layerIndex] = event.startSec + clipSec;
        }

        return { event, layerIndex };
      });

      return {
        groupKey,
        events: layered,
        layerCount: layerEnds.length,
        earliest: sorted[0]?.startSec ?? 0,
      };
    })
    .sort((a, b) => a.earliest - b.earliest)
    .map(({ groupKey, events: layered, layerCount }) => ({
      groupKey,
      events: layered,
      layerCount,
    }));
}

/**
 * ### CompositeSpan
 *
 * A stretch of the timeline, as percentages across the row.
 */
export type CompositeSpan = {
  leftPercent: number;
  widthPercent: number;
};

/**
 * ### verdictSpan
 *
 * The stretch the detections cover, from the first one starting to the last
 * one ending. It is what the rule matched over, so the verdict is drawn
 * across exactly that much of the timeline rather than the whole row.
 *
 * @param events Detections of the composite event
 * @param clipSec Drawn length of every clip
 * @param durationSec Timeline length in seconds
 * @returns Where the verdict bar starts and how wide it is
 */
export function verdictSpan(
  events: ReadonlyArray<CompositeScenarioEvent>,
  clipSec: number,
  durationSec: number,
): CompositeSpan {
  const starts = events.map((event) => event.startSec);
  const ends = events.map((event) =>
    Math.min(durationSec, event.startSec + clipSec),
  );
  const from = Math.min(...starts);
  const to = Math.max(...ends);

  return {
    leftPercent: (from / durationSec) * 100,
    widthPercent: ((to - from) / durationSec) * 100,
  };
}

/**
 * ### CompositeRuleTitle
 *
 * A rule name as the pop-up sets it: the situation, and the qualifier the
 * name carries in parentheses, if any.
 */
export type CompositeRuleTitle = {
  main: string;
  qualifier: string | null;
};

/**
 * ### splitRuleName
 *
 * Splits a rule name such as "위협 상황 (폭행 의심)" into the situation and its
 * parenthesised qualifier, so the pop-up can set the situation at display
 * size and the qualifier on a smaller line beneath it. A name with no
 * trailing parenthesis comes back whole.
 *
 * @param name Translated rule name
 * @returns The situation and the qualifier without its parentheses
 */
export function splitRuleName(name: string): CompositeRuleTitle {
  const match = /^(.*\S)\s*\(([^()]+)\)\s*$/.exec(name);

  return match
    ? { main: match[1], qualifier: match[2].trim() }
    : { main: name, qualifier: null };
}
